import { toast } from 'react-toastify';

export type DonorSlipKind = 'donor' | 'guest';

interface DonorLike {
  donorId?: string;
  guestId?: string;
  name?: string;
  fatherOrHusbandName?: string;
  relation?: string;
  cnic?: string;
  phone?: string;
  email?: string;
  address?: string;
  officeId?: unknown;
}

/**
 * Print a donor / guest-donor registration slip (basic details + a large,
 * highlighted Donor ID) after the record has been created.
 *
 * Printing is best-effort: the record already exists server-side, so a print
 * failure (e.g. printer offline) never rolls it back — we surface a warning
 * with the assigned ID instead so the operator can note it manually.
 */
export async function printDonorRegistrationSlip(donor: DonorLike, kind: DonorSlipKind): Promise<void> {
  const api = (window as Window & { electronAPI?: any }).electronAPI;
  const id = kind === 'guest' ? donor.guestId : donor.donorId;
  const noun = kind === 'guest' ? 'Guest donor' : 'Donor';

  // Web build / no desktop bridge — nothing to print.
  if (!api?.printDonorReceipt) {
    toast.success(`${noun} created successfully${id ? ` (ID: ${id})` : ''}!`);
    return;
  }

  let language: 'en' | 'ur' = 'en';
  let slipPrinter = '';
  try {
    const prefs = JSON.parse(localStorage.getItem('hria-prefs') || '{}');
    if (prefs.reportLanguage === 'en' || prefs.reportLanguage === 'ur') language = prefs.reportLanguage;
    if (prefs.slipPrinter) slipPrinter = prefs.slipPrinter;
  } catch {
    // fall back to defaults
  }

  const office = donor.officeId as { name?: string; officeNo?: number } | string | undefined;
  const officeName = office && typeof office === 'object'
    ? `${office.name ?? ''}${office.officeNo != null ? ` (Office #${office.officeNo})` : ''}`.trim()
    : '';

  try {
    const res = await api.printDonorReceipt({
      kind,
      donorId: id,
      name: donor.name,
      fatherOrHusbandName: donor.fatherOrHusbandName,
      relation: donor.relation,
      cnic: donor.cnic,
      phone: donor.phone,
      email: donor.email,
      address: donor.address,
      officeName,
      language,
      slipPrinter,
    });

    if (res?.success) {
      toast.success(`${noun} ${id ?? ''} created — registration slip printed.`.replace('  ', ' '));
    } else {
      toast.warning(`${noun} ${id ?? ''} created, but the slip did not print: ${res?.error || 'unknown error'}`);
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'unknown error';
    toast.warning(`${noun} ${id ?? ''} created, but printing failed: ${message}`);
  }
}
